import { Dispatch, SetStateAction, useEffect } from 'react';
import { UNIT_MIX_DEFAULT_NAME } from 'views/ProFormaWizard/constants';
import { ITenant, IUnitMix, TUnit } from '../types';

interface UseUnitManagerProps {
    unitArray: IUnitMix[];
    tenantArray: ITenant[];
    setUnitArray: Dispatch<SetStateAction<IUnitMix[]>>;
    setTenantArray: Dispatch<SetStateAction<ITenant[]>>;
    initialUnits: IUnitMix[];
    initialTenants: ITenant[];
    isApartment: boolean;
}

export const useUnitManager = ({
    unitArray, setUnitArray, initialUnits,
    tenantArray, setTenantArray, initialTenants,
    isApartment,
}: UseUnitManagerProps) => {
    const defaultUnitMix = {
        name: UNIT_MIX_DEFAULT_NAME,
        averageSquareFootage: 0,
        amountPerMonth: 0,
        units: 0,
        totalRent: 0,
    };

    const defaultTenant = {
        tenant: UNIT_MIX_DEFAULT_NAME,
        suite: '',
        squareFootage: 0,
        rentGrowth: 0,
        valuePerSfPerYear: 0,
        leaseStart: '',
        rentStart: '',
        recoveryPercentage: 0,
        recoveryStart: '',
    };

    const defaultUnit = isApartment ? defaultUnitMix : defaultTenant;

    const handleDuplicateOrAdd = (unitId?: number) => {
        const targetList = isApartment ? unitArray : tenantArray;
        const targetFunc = isApartment ? setUnitArray : setTenantArray;
        const lowestNegativeId = (targetList as TUnit[]).reduce((min, unit) => (unit.id < min ? unit.id : min), 0);
        const newId = lowestNegativeId > 0 ? -1 : lowestNegativeId - 1;

        if (unitId) {
            const unitToDuplicate = (targetList as TUnit[]).find(unit => unit.id === unitId);
            if (unitToDuplicate) {
                targetFunc([...targetList, { ...unitToDuplicate, id: newId }] as any);
            }
        } else {
            targetFunc([...targetList, { ...defaultUnit, id: newId }] as any);
        }
    };

    const handleDelete = (unitId: number) => {
        if (isApartment) {
            setUnitArray(prev => prev.filter(unit => unit.id !== unitId));
        } else {
            setTenantArray(prev => prev.filter(unit => unit.id !== unitId));
        }
    };

    const handleUpdate = (updatedUnit: TUnit) => {
        if (isApartment) {
            setUnitArray(prev => prev.map(unit => (unit.id === updatedUnit.id ? { ...unit, ...updatedUnit } : unit) as IUnitMix));
        } else {
            setTenantArray(prev => prev.map(unit => (unit.id === updatedUnit.id ? { ...unit, ...updatedUnit } : unit) as ITenant));
        }
    };

    useEffect(() => {
        if (initialUnits.length > 0) {
            const newUnits = initialUnits.map(unit => ({
                ...unit,
                id: unit.id,
                name: unit.name,
                averageSquareFootage: Number(unit.averageSquareFootage) || 0,
                amountPerMonth: Number(unit.amountPerMonth) || 0,
                units: Number(unit.units) || 0,
                totalRent: Number(unit.totalRent) || 0,
            }));
            setUnitArray(newUnits);
        } else {
            setUnitArray([{ ...defaultUnit, id: -1 }] as IUnitMix[]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialUnits]);

    useEffect(() => {
        if (initialTenants.length > 0) {
            const newTenants = initialTenants.map(tenant => ({
                ...tenant,
                id: tenant.id,
                tenant: tenant.tenant || UNIT_MIX_DEFAULT_NAME,
                suite: tenant.suite || '',
                squareFootage: tenant.squareFootage || 0,
                rentGrowth: tenant.rentGrowth || 0,
                valuePerSfPerYear: tenant.valuePerSfPerYear || 0,
                leaseStart: tenant.leaseStart ? tenant.leaseStart : '',
                rentStart: tenant.rentStart ? tenant.rentStart : '',
                recoveryPercentage: tenant.recoveryPercentage || 0,
                recoveryStart: tenant.recoveryStart ? tenant.recoveryStart : '',
            }));

            setTenantArray(newTenants);
        } else {
            setTenantArray([{ ...defaultTenant, id: -1 }]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialTenants]);

    return {
        unitArray,
        tenantArray,
        handleDuplicateOrAdd,
        handleDelete,
        handleUpdate,
        setUnitArray,
        setTenantArray,
    };
};
